import type { Theme } from '../../contract.js';
import { darkElectricLight, darkElectricDark } from './variants.js';

/**
 * Dark Electric family — pure-black chrome for drivers who run
 * the PWA in low-light environments (overnight charging) or
 * prefer the OLED-friendly aesthetic. Net-new in the Driver
 * Themes ticket. Pairs the bespoke black dark variant with the
 * Mapbox Dark basemap; the light variant inherits Default light
 * + satellite for daytime use.
 *
 * Routes the ChargingAnimation to the orb shader (dark-electric
 * variant — matte black sphere with bright halo per the
 * per-variant uniforms in Commit 5). The matte-black orb pairs
 * with the dark basemap to give the surface its OLED-friendly
 * "everything is black except the energy" identity.
 */
export const darkElectricTheme: Theme = {
  id: 'dark-electric',
  name: 'Dark Electric',
  description: 'Pure-black chrome with greyscale smoke motion.',
  // Greyscale rework swatches (post-C11). The picker's per-row
  // SwatchRow reads variant.tokens.colors.{accent,charging,surface}
  // directly and ignores this top-level array; it's the catalogue/
  // seed-display swatch (see packages/ui DriverThemePicker). Updated to
  // match the new identity so seed output stays in sync.
  swatches: ['#000000', '#8C8C8C', '#3A3A40'],
  variants: { light: darkElectricLight, dark: darkElectricDark },
  unlockedByDefault: true,
  costPoints: 0,
  // Dark-only family (single-mode infra, commit 4). The pure-black OLED
  // darkElectricDark variant IS the identity; the provider clamp resolves
  // Dark Electric to variants['dark'] regardless of the saved mode, and
  // ModeSegment shows only the Dark button. darkElectricLight stays below
  // (the contract requires both variants) but is now unreachable.
  availableModes: ['dark'],
  // Post-greyscale-rework map style decision — both modes paint the
  // Mapbox Dark basemap. Previous default sent light mode to
  // satellite-streets; Warren's product call after the rework is
  // that the family identity is OLED-friendly black-and-grey, and
  // the "soft dark" light variant's dark-grey chrome reads more
  // naturally over the flat Mapbox Dark basemap than over satellite.
  mapStyle: {
    light: 'mapbox://styles/mapbox/dark-v11',
    dark: 'mapbox://styles/mapbox/dark-v11',
  },
  // Theme charging sims v2 (Warren pass 2): the physarum network read as
  // too static on device, so Dark Electric moves to the interactive fluid
  // in its own personality — the reference WebGL-Fluid-Simulation feel,
  // silver/white/grey dye blooming on pure black with strong swirl and a
  // lively fade. Same engine as Electric Light, deliberately different
  // fluidity (fast and luminous vs slow inky marbling). The physarum sim
  // stays built and wired for a future theme/skin. Dark-only family, so
  // the single variant needs no dispatcher mode resolution.
  chargingAnimation: 'shader',
  shaderKind: 'fluid',
  shaderVariant: 'fluid-electric-dark',
};
